import store from "../store";
import RequestService from "./RequestService";

let TRANSLATION_IS_PROCESSING;

export default {
    removeKanjiSpaces(text) {
        if (!text || typeof text !== "string") {
            return text;
        }
        // Remove spaces between kanji characters (CJK Unified Ideographs)
        return text.replace(/([\u4E00-\u9FFF])\s+(?=[\u4E00-\u9FFF])/g, '$1');
    },
    fixGreekLetters(text) {
        if (!text || typeof text !== "string") {
            return text;
        }
        return text.replace(/[Α-Ωα-ωА-Яа-я]/g, (s) => {
            switch (s) {
                // Greek uppercase letters
                case 'Α': return 'A';
                case 'Β': return 'B';
                case 'Ε': return 'E';
                case 'Η': return 'H';
                case 'Ι': return 'I';
                case 'Κ': return 'K';
                case 'Μ': return 'M';
                case 'Ν': return 'N';
                case 'Ο': return 'O';
                case 'Ρ': return 'P';
                case 'Τ': return 'T';
                case 'Χ': return 'X';
                case 'Υ': return 'Y';
                case 'Ζ': return 'Z';
                // Greek lowercase letters
                case 'ν': return 'v';
                case 'ο': return 'o';
                // Cyrillic uppercase letters that look like Latin
                case 'А': return 'A';
                case 'В': return 'B';
                case 'Е': return 'E';
                case 'І': return 'I';
                case 'Ј': return 'J';
                case 'К': return 'K';
                case 'М': return 'M';
                case 'Н': return 'H';
                case 'О': return 'O';
                case 'Р': return 'P';
                case 'С': return 'C';
                case 'Т': return 'T';
                case 'Х': return 'X';
                case 'Ѕ': return 'S';
                // Cyrillic lowercase letters that look like Latin
                case 'а': return 'a';
                case 'е': return 'e';
                case 'і': return 'i';
                case 'ј': return 'j';
                case 'о': return 'o';
                case 'р': return 'p';
                case 'с': return 'c';
                case 'ѕ': return 's';
                case 'у': return 'y';
                case 'х': return 'x';
                default: return s;
            }
        });
    },
    getRoleNames(staff) {
        let firstEpisode;
        const roleNames = new Set();
        
        for (const episode of store.state.staffList.menus) {
            for (const category of episode.credits) {
                for (const role of category.roles) {
                    for (const person of role.staff) {
                        if (person.ja == staff.ja && person.en == staff.en) {
                            firstEpisode = episode;
                            let roles;
                            if (role.name) {
                                roles = role.name.split(/[\s]+\/[\s]+/g);
                            } else if (role.original) {
                                roles = role.original.split(/・/g);
                            } else {
                                continue;
                            }

                            for (const r of roles) {
                                if (r) {
                                    roleNames.add(r);
                                }
                            }
                        }
                    }
                }
            }
        }

        return [ [...roleNames], firstEpisode ];
    },
    updateStaff(updatedStaff, attributes, checkCondition) {
        if (!updatedStaff) return;
        if (!updatedStaff.ja && !updatedStaff.en) return;

        const presentOrDeleteAttributes = ["isStudio", "isNC", "pn", "id"];
        const studioAttributes = ["isNC", "ja", "en", "id"];

        // No autolink: update the staff directly and return
        if (!store.state.staffList.settings.autoLink) {
            for (const attribute in attributes) {
                if (presentOrDeleteAttributes.includes(attribute) && !attributes[attribute]) {
                    delete updatedStaff[attribute];
                    continue;
                }

                updatedStaff[attribute] = attributes[attribute];
            }

            return;
        }

        if (!checkCondition) {
            const updatedStaffId = updatedStaff.id;
            const updatedStaffJa = updatedStaff.ja;
            const updatedStaffEn = updatedStaff.en;

            checkCondition = (staff) => {
                return (
                    (!staff.id || staff.id == updatedStaffId) && (
                        (staff.ja == updatedStaffJa && updatedStaffJa)
                        || (staff.en == updatedStaffEn && !updatedStaffJa && !staff.ja)
                    )
                );
            };
        }
        
        if (updatedStaff.scope == "local") {
            for (const attribute in attributes) {
                if (presentOrDeleteAttributes.includes(attribute) && !attributes[attribute]) {
                    delete updatedStaff[attribute];
                    continue;
                }
    
                updatedStaff[attribute] = attributes[attribute];
            }
            
            return;
        }

        for (const episode of store.state.staffList.menus) {
            for (const category of episode.credits) {
                for (const role of category.roles) {
                    for (const staff of role.staff) {
                        if (checkCondition(staff)) {
                            for (const attribute in attributes) {
                                if (presentOrDeleteAttributes.includes(attribute) && !attributes[attribute]) {
                                    delete staff[attribute];
                                    continue;
                                }

                                staff[attribute] = attributes[attribute];
                            }
                        }

                        // Studio affiliation sync
                        if (staff.studio && typeof staff.studio == "object" && checkCondition(staff.studio)) {
                            for (const attribute in attributes) {
                                if (studioAttributes.includes(attribute) && !attributes[attribute]) {
                                    delete staff.studio[attribute];
                                    continue;
                                }

                                staff.studio[attribute] = attributes[attribute];
                            }
                        }
                    }
                }
            }
        }
    },
    async updateID(staff, id) {
        this.updateStaff(staff, {
            id: id,
            ...(!id && { pn: null })
        });

        if (!id) {
            return;
        }

        const res = await fetch(`${import.meta.env.VITE_API_URL}/api/person/get_by_id.php?id=${encodeURIComponent(id)}&studio=${staff.isStudio ? 1 : 0}`);
        if (res.status === 404) {
            // Unsync
            this.updateStaff(staff, {
                id: null,
                pn: null
            });

            store.commit('addAlert', {
                id: "id-not-found-" + id,
                title: "Error",
                text: "The provided ID was not found on KeyFrame Staff List. Please check the ID and try again.\n\n**Note:** AniList-only IDs aren't supported anymore. A moderator will create a page for the staff if it doesn't exist."
            });
            return;
        }
        if (res.status === 429) {
            console.warn("Rate limited by AniList API, retrying in 30 seconds", res);
            await new Promise(resolve => setTimeout(resolve, 30000));
            return await this.updateID(staff, id);
        }

        const data = await res.json();
        if (!data || (!data.ja && !data.en)) {
            store.commit('addAlert', {
                id: "id-not-found-" + id,
                title: "Error",
                text: "The provided ID was not found on KeyFrame Staff List. Please check the ID and try again.\n\n**Note:** AniList-only IDs aren't supported anymore. A moderator will create a page for the staff if it doesn't exist."
            });
            return;
        }

        // console.log("data is ", data);

        const ja = staff.ja || "";
        const en = staff.en || "";
        const pageJa = data.ja || "";
        const pageEn = data.en || "";

        const staffData = {};

        if (
            // Staff doesn't have an English name and the page has one
            (!en && pageEn) ||
            // Staff has the same Japanese name as the page but a different English name
            (ja && ja == pageJa && en != pageEn)
        ) {
            // Update/Fix English translation
            staffData.en = pageEn;
        }

        if (
            // The staff Japanese name is different from the page Japanese name (covers both cases where staff has/doesn't have a Japanese name) and the page has a Japanese name
            (ja != pageJa) ||
            // The staff has an English name only that is different from the page English name
            (!ja && !this.equalCaseInsensitive(en, pageEn))
        ) {
            staffData.pn = {
                ja: pageJa,
                en: pageEn
            };
        } else if (staff.pn) {
            // Erase pen name if not needed
            staffData.pn = null;
        }

        // console.log("Updating staff data:", staffData, staff, ja, en, pageJa, pageEn);
        this.updateStaff(staff, staffData);
    },
    async validateID(id, isStudio) {
        const res = await fetch(`${import.meta.env.VITE_API_URL}/api/person/get_by_id.php?id=${encodeURIComponent(id)}&studio=${isStudio ? 1 : 0}`);
        if (res.status === 429) {
            console.warn("Rate limited by AniList API, retrying in 30 seconds", res);
            await new Promise(resolve => setTimeout(resolve, 30000));
            return await this.validateID(id, isStudio);
        }

        if (res.status === 404) {
            store.commit('addAlert', {
                id: "id-not-found-" + id,
                title: "Error",
                text: "The provided ID was not found on KeyFrame Staff List. Please check the ID and try again.\n\n**Note:** AniList-only IDs aren't supported anymore. A moderator will create a page for the staff if it doesn't exist."
            });
            return false;
        }

        if (!res.ok) {
            throw new Error(`Failed to validate ID ${id}: HTTP ${res.status}`);
        }

        const data = await res.json();
        if (!data || (!data.ja && !data.en)) {
            store.commit('addAlert', {
                id: "id-not-found-" + id,
                title: "Error",
                text: "The provided ID was not found on KeyFrame Staff List. Please check the ID and try again.\n\n**Note:** AniList-only IDs aren't supported anymore. A moderator will create a page for the staff if it doesn't exist."
            });
            return false;
        }

        return true;
    },
    isSameStaff(staff1, staff2) {
        if (staff1.id && staff2.id) {
            return staff1.id == staff2.id;
        }

        if (staff1.ja && staff2.ja) {
            return staff1.ja == staff2.ja;
        }

        if (!staff1.ja && !staff2.ja && staff1.en && staff2.en) {
            return staff1.en == staff2.en;
        }

        return false;
    },
    equalCaseInsensitive(string1, string2) {
        return (string1 || "").toLocaleLowerCase() == (string2 || "").toLocaleLowerCase();
    },
    addToTranslationQueue(staff, mode, position, category) {
        if (staff.untranslated) {
            delete staff.untranslated;
        }

        // Full romaji (ex: studio names, non-Japanese)
        if (staff.ja && !staff.en && /^[A-Za-zÀ-ÖØ-öø-ÿĀ-žƀ-ɏḀ-ỿ0-9 .,:;!?()\[\]{}'"’‘“”\/\\_\-°•・･℃@＠#$☆△▲%&*+=^|]+$/g.test(staff.ja)) {
            [staff.ja, staff.en] = [staff.en, staff.ja];
        }

        // If local translation
        if (mode == "lazy" && store.state.staffList.settings.autoLink && this.findExistingTranslation(staff, "lazy")) {
            if (staff.callback) staff.callback();
            return;
        }
        
        // If already in queue
        if (store.state.staffList.settings.autoLink && !staff.callback && store.state.translationQueue.find(q => this.isSameStaff(q.staff, staff))) {
            staff.inQueue = true;
            return;
        }

        // If empty
        if (!staff.ja && !staff.en) {
            if (staff.callback) staff.callback();
            return;
        }

        if (!store.state.staffList.settings.autoTranslation) {
            if (staff.callback) staff.callback();
            return;
        }
        
        // console.log("Adding to queue", staff);
        staff.inQueue = true;

        if (position == "top") {
            store.state.translationQueue.unshift({
                staff: staff,
                mode: mode || "lazy",
                category: category || null
            });
        } else {
            store.state.translationQueue.push({
                staff: staff,
                mode: mode || "lazy",
                category: category || null
            });
        }
        
        store.state.translationTotal += 1;

        if (!TRANSLATION_IS_PROCESSING) {
            this.processQueue();
        }
    },
    async processQueue() {
        if (TRANSLATION_IS_PROCESSING) {
            return;
        }

        TRANSLATION_IS_PROCESSING = true;

        while (store.state.translationQueue.length > 0) {
            const { staff, mode, category } = store.state.translationQueue.shift();

            // console.log("Processing", staff, "with mode", mode);

            // If deleted
            if (staff.deleted) {
                continue;
            }

            // If local translation
            if (mode == "lazy" && this.findExistingTranslation(staff, mode)) {
                this.clearInQueueStatus(staff);
                
                if (staff.callback) staff.callback();
                continue;
            }

            // If empty
            if (!staff.ja && !staff.en) {
                this.clearInQueueStatus(staff);

                if (staff.callback) staff.callback();
                continue;
            }

            try {
                const status = await this.findTranslation(staff, category);
                this.clearInQueueStatus(staff);
                
                if (status !== 3 && staff.callback) staff.callback();
            
                // Wait for status 2 (API call) to respect AniList rate limit
                if (status === 2) {
                    await this.sleep(store.state.isContributor ? 100 : 800);
                }
            } catch (e) {
                console.error("Received an error while processing the queue:", e);

                store.commit('addAlert', {
                    id: "process-queue",
                    title: "Error",
                    text: "Could not fetch the translation. This might be because you are rate limited. Please try again later."
                });

                this.clearInQueueStatus(staff);
                
                if (staff.callback) staff.callback();
                
                // Empty queue and stop processing
                this.emptyQueue();
                break;
            }
        }

        TRANSLATION_IS_PROCESSING = false;
    },
    clearInQueueStatus(staff) {
        delete staff.inQueue;

        if (staff.callback) {
            return;
        }

        // Find staff with the same name and remove the inQueue status
        for (const episode of store.state.staffList.menus) {
            for (const category of episode.credits) {
                for (const role of category.roles) {
                    for (const person of role.staff) {
                        if (this.isSameStaff(person, staff)) {
                            delete person.inQueue;
                        }
                    }
                }
            }
        }
    },
    emptyQueue() {
        // Delete all in queue status
        for (const episode of store.state.staffList.menus) {
            for (const category of episode.credits) {
                for (const role of category.roles) {
                    for (const staff of role.staff) {
                        if (staff.inQueue) {
                            delete staff.inQueue;
                        }
                    }
                }
            }
        }
        store.state.translationQueue.length = 0;

        store.state.translationTotal = 0;
    },
    findExistingTranslation(staff, mode) {
        const hasSameMainNames = (candidate, target) => {
            const candidateJa = (candidate?.ja || "").trim();
            const candidateEn = (candidate?.en || "").trim();
            const targetJa = (target?.ja || "").trim();
            const targetEn = (target?.en || "").trim();

            const sameJa = candidateJa !== "" && targetJa !== "" && candidateJa === targetJa;
            const sameEnWithEmptyJa =
                candidateEn !== "" &&
                targetEn !== "" &&
                candidateEn === targetEn &&
                candidateJa === "" &&
                targetJa === "";

            return sameJa || sameEnWithEmptyJa;
        };

        for (let episode of store.state.staffList.menus) {
            for (let category of episode.credits) {
                for (let role of category.roles) {
                    for (let person of role.staff) {
                        if (person != staff && hasSameMainNames(person, staff)) {
                            this.updateStaff(staff, {
                                id: person.id,
                                pn: person.pn,
                                isStudio: person.isStudio,
                                ...(person.en && staff.ja && { en: person.en })
                            });

                            // No need to continue if the AniList ID is found
                            if (person.id) {
                                return true;
                            }

                            if (mode == "lazy") {
                                // In lazy mode, only skip translation if an ID was found
                                // This ensures we fetch IDs from the API when they don't exist locally
                                // The API cache will prevent duplicate requests
                                if (person.id) {
                                    return true;
                                }
                                
                                // No ID found, continue to translation
                                return this.studioCheck(staff);
                            }
                        }
                    }
                }
            }
        }

        // Studio names
        return this.studioCheck(staff);
    },
    studioCheck(staff) {
        if (staff.ja) {
            const studioNameWithoutSuffix = staff.ja.replace(/(有限責任会社|股份有限公司|有限公司|公司|有限会社|株式会社|合同会社|\(株\)|\(有\)|㈲)/g, "");

            // If the Japanese name has a studio suffix
            if (studioNameWithoutSuffix != staff.ja) {
                // If the studio name is already in English with a Japanese suffix
                if (/^[A-Za-zÀ-ÖØ-öø-ÿĀ-žƀ-ɏḀ-ỿ0-9 .,:;!?()\[\]{}'"’‘“”\/\\_\-°•・･℃@＠#$☆△▲%&*+=^|]+$/g.test(studioNameWithoutSuffix)) {
                    this.updateStaff(staff, {
                        isStudio: true,
                        en: studioNameWithoutSuffix.trim()
                    });
                } else {
                    this.updateStaff(staff, {
                        isStudio: true
                    });
                }
            }
        }

        return false;
    },
    async findTranslation(staff, category = null) {
        if (this.findExistingTranslation(staff)) {
            return 1;
        }

        if (!staff.ja && !staff.en) {
            return 0;
        }
        if ((staff.ja || "").trim() == "" && (staff.en || "").trim() == "") {
            return 0;
        }
        
        let result = await this.combineTranslations(staff, 0, category);

        if (result === "conflict" || result === "cache-conflict") {
            return 3;
        }

        return (result === "cache") ? 4 : 2;
    },
    async combineTranslations(staff, retryCount = 0, category = null) {
        const keyFrameResponse = await this.translateNameWithKeyFrame(staff, category);

        // If both requests failed, retry after cooldown
        if (keyFrameResponse?.failed) {
            console.warn(`Translation failed for "${staff.ja || staff.en}". Retrying in 5 seconds... (attempt ${retryCount + 1})`);
            await this.sleep(5000);
            return await this.combineTranslations(staff, retryCount + 1, category);
        }

        // Exit if the row was deleted!!
        if (!staff.ja && !staff.en) {
            return false;
        }

        let kf = keyFrameResponse?.results || [];
        console.log("Translation responses for", staff, ":", kf);

        if (kf.length === 0) {
            return keyFrameResponse?.fromCache ? "cache" : false;
        }

        // Conflict window is needed
        // More than 1 result or Korean simplified name match
        const hasKoreanMatch = kf.some(m => m.source === "2");
        if (kf.length > 1 || (kf.length === 1 && hasKoreanMatch)) {
            if (!store.state.staffList.settings.showConflicts) {
                return;
            }

            const [ roleNames, firstEpisode ] = this.getRoleNames(staff);

            // Provide a stable selection id for KF items without AniList id
            let kfCounter = 1;
            for (const item of kf) {
                item._select_id = `KF-${kfCounter++}`;
            }

            store.commit('addConflict', {
                id: "conflict-" + (staff.ja || staff.en),
                origin: staff.ja || staff.en,
                episode: firstEpisode,
                roleNames: roleNames.join(", "),
                data: kf,
                staff: staff,
                callback: (data) => {
                    if (!data) {
                        if (staff.callback) staff.callback();
                        return;
                    }

                    const selected = kf.find(m => (m._select_id && m._select_id == data._select_id));

                    if (!selected) {
                        if (staff.callback) staff.callback();
                        return;
                    }

                    const s = selected.raw;

                    // Check if server returned ja without kanji spaces
                    const shouldUpdateJa = staff.ja && s.ja && s.ja === this.removeKanjiSpaces(staff.ja) && s.ja !== staff.ja;

                    // Auto pen name for EN only
                    if (!staff.pn && ((!staff.ja && s.ja) || !this.equalCaseInsensitive(staff.en, s.en))) {
                        s.pn = { ja: s.ja || "", en: s.en || "" };
                    }

                    const shouldSetPnFromMain = !staff.ja && (s.ja && s.en);

                    this.updateStaff(staff, {
                        ...(s.en && (staff.ja) && { en: s.en }),
                        ...(shouldSetPnFromMain && { pn: { ja: s.ja || "", en: s.en || "" } }),
                        ...((s.pn_ja || s.pn_en) && { pn: { ja: s.pn_ja, en: s.pn_en } }),
                        ...(s.anilist_id && { id: s.anilist_id }),
                        ...(s.is_studio && { isStudio: true }),
                        ...(shouldUpdateJa && { ja: s.ja })
                    });

                    if (staff.callback) staff.callback();
                }
            });

            await this.sleep(store.state.isContributor ? 100 : 1000);

            return keyFrameResponse?.fromCache ? "cache-conflict" : "conflict";
        }

        // Single candidate: apply it immediately
        const selected = kf[0];
        const s = selected.raw;
        
        // Check if server returned ja without kanji spaces
        const shouldUpdateJa = staff.ja && s.ja && s.ja === this.removeKanjiSpaces(staff.ja) && s.ja !== staff.ja;

        // Auto pen name for EN only
        if (!staff.pn && ((!staff.ja && s.ja) || (staff.en != s.en))) {
            s.pn = { ja: s.ja || "", en: s.en || "" };
        }
        
        const shouldSetPnFromMain = !staff.ja && (s.ja && s.en);

        this.updateStaff(staff, {
            ...(s.en && (staff.ja) && { en: s.en }),
            ...(shouldSetPnFromMain && { pn: { ja: s.ja || "", en: s.en || "" } }),
            ...((s.pn_ja || s.pn_en) && { pn: { ja: s.pn_ja, en: s.pn_en } }),
            ...(s.anilist_id && { id: s.anilist_id }),
            ...(s.is_studio && { isStudio: true }),
            ...(shouldUpdateJa && { ja: s.ja })
        });

        return keyFrameResponse?.fromCache ? "cache" : true;
    },
    async translateNameWithKeyFrame(staff, category = null) {
        const language = staff.ja ? "ja" : "en";
        const nameQuery = staff[language];

        if (!nameQuery) {
            return { results: [], fromCache: true };
        }

        // Create a case-insensitive cache key that includes language, query and category
        const cacheKey = `${language}:${nameQuery.toLowerCase()}:${category || ''}`;

        let results;
        let fromCache = false;

        // Check if we have a cached result for this nameQuery
        if (store.state.keyFrameTranslationCache.hasOwnProperty(cacheKey)) {
            results = store.state.keyFrameTranslationCache[cacheKey];
            fromCache = true;
        } else {
            // Make the API call since we don't have cached results
            const categoryParam = category ? `&category=${encodeURIComponent(category)}` : '';
            results = await RequestService.fetchNoCooldown(`${import.meta.env.VITE_API_URL}/api/data/translate.v4.php?${language}=${encodeURIComponent(staff[language])}&uuid=${store.state.staffList.uuid}${categoryParam}`);
            
            // Only cache if the request was successful (not null)
            // Failed requests return null and should not be cached
            if (results !== null) {
                store.commit('setKeyFrameTranslationCache', {
                    key: cacheKey,
                    value: results || []
                });
            }
        }
        
        // If the fetch failed (results is null), return with failed flag
        if (results === null) {
            return { results: [], fromCache: false, failed: true };
        }

        if (!results || results.length == 0) {
            return { results: [], fromCache };
        }

        const normalized = [];
        for (let i = 0; i < results.length; i++) {
            const r = results[i];
            const pn_ja = (r.pn_ja || "").trim();
            const pn_en = (r.pn_en || "").trim();

            normalized.push({
                id: r.anilist_id || "",
                ja: r.ja || "",
                en: r.en || "",
                ...( (pn_ja || pn_en || (!staff.ja)) && { pn: { ja: pn_ja, en: pn_en } } ),
                isStudio: !!r.is_studio,
                from: 'KFSL',
                source: r.source || "1",
                raw: r
            });
        }

        return { results: normalized, fromCache };
    },
    async findName(column, staff) {
        const results = [];
        const names = [];

        for (let episode of store.state.staffList.menus) {
            for (let category of episode.credits) {
                for (let role of category.roles) {
                    for (let person of role.staff) {
                        if (person[column] && names.indexOf(`${person.ja};${person.en}`) == -1 && person[column].indexOf(staff[column]) != -1 && person != staff) {
                            names.push(`${person.ja};${person.en}`);
                            results.push(person);
                        }
                    }
                }
            }
        }
        return results;
    },
    async sleep(time) {
        return new Promise((resolve) => setTimeout(resolve, time));
    },
    calculateStats(selectedMenu, selectedRole) {
        var rolesAppearances = {}; // Key: role of name, value: appearences of a certain role to calculate the average number of staff for each role
        var rolesTotal = [];
        var staffTotal = [];

        // console.log(selectedRole);

        for (let ep of store.state.staffList.menus) {
            if (ep.name == "Overview") {
                continue;
            }

            if (selectedMenu && 
                (selectedMenu == "episodes" && !ep.name.startsWith("#")) ||
                (selectedMenu == "specials" && ep.name.startsWith("#"))
            ) {
                continue;
            }

            for (let credit of ep.credits) {
                for (let role of credit.roles) {
                    const staffCount = role.staff.reduce((total, staff) => {
                        return total + (staff.ja || staff.en ? 1 : 0);
                    }, 0);

                    let staffIsAccepted = false;

                    const splitRoles = role.name.split(/[\s]+\/[\s]+/g);
                    for (let roleName of splitRoles) {
                        if (selectedRole && roleName != selectedRole) {
                            continue;
                        }

                        // Update or add a new entry to rolesTotal
                        var roleIndex = rolesTotal.findIndex(r => {
                            return r.name === roleName
                        });
                        if (roleIndex === -1) {
                            rolesTotal.push({
                                name: roleName,
                                avg_staff: staffCount,
                                count: 1
                            });
                        } else {
                            rolesTotal[roleIndex].avg_staff += staffCount;
                            rolesTotal[roleIndex].count += 1;
                        }

                        // Increment the appearance of the current role
                        rolesAppearances[roleName] = (rolesAppearances[roleName] || 0) + 1;
                        staffIsAccepted = true;
                    }

                    if (!staffIsAccepted) {
                        continue;
                    }

                    // Update or add a new entry to staffTotal
                    for (let staff of role.staff) {
                        if (staff.ja == "" && staff.en == "") {
                            continue;
                        }

                        var staffIndex = staffTotal.findIndex(s => {
                            return s.ja === staff.ja && s.en === staff.en
                        });
                        if (staffIndex === -1) {
                            staffTotal.push({
                                ja: staff.ja,
                                en: staff.en,
                                isStudio: staff.isStudio,
                                appearances: 1
                            });
                        } else {
                            staffTotal[staffIndex].appearances += 1;
                        }
                    }
                }
            }
        }

        // Calculate the average number of staff for each role in rolesTotal
        for (let role of rolesTotal) {
            role.avg_staff = Math.round((role.avg_staff / role.count)*10)/10;
        }

        // Sort rolesTotal by average staff count in descending order
        rolesTotal.sort((a, b) => {
            return b.avg_staff - a.avg_staff;
        });

        // Sort staffTotal by Japanese name in ascending order
        staffTotal.sort((a, b) => {
            return b.appearances - a.appearances;
        });

        return {
            staff: staffTotal,
            roles: rolesTotal
        };
    },
    allNames() {
        /**
         * Returns all unique names from the staff list.
         * Uses a Map with string keys for efficient deduplication.
         */
        const namesMap = new Map();

        for (let episode of store.state.staffList.menus) {
            for (let category of episode.credits) {
                for (let role of category.roles) {
                    for (let staff of role.staff) {
                        // Skip empty names
                        if (!staff.ja && !staff.en) continue;

                        const key = `${staff.id || ''}|${staff.ja || ''}|${staff.en || ''}|${staff.isStudio ? '1' : '0'}`;
                        
                        if (!namesMap.has(key)) {
                            namesMap.set(key, {
                                ...staff,
                            });
                        }

                        if (staff.studio) {
                            const studioKey = `${staff.studio.id || ''}|${staff.studio.ja || ''}|${staff.studio.en || ''}|1`;
                            
                            if (!namesMap.has(studioKey)) {
                                namesMap.set(studioKey, {
                                    ...staff.studio,
                                    isStudio: true,
                                });
                            }
                        }
                    }
                }
            }
        }

        return Array.from(namesMap.values());
    }
}